import { call, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import * as api from '@api/invitations';
import { actions } from './invitationsUserManagement.slice';
import {
  InvitationsListFetchAction,
  CreateInvitationRequest,
  ApiError,
} from './invitationsUserManagement.types';
import { Resp, dispatch } from '../helpers';
import { runToast } from '../tools';

const messages = {
  createInvitation: {
    success: 'Invitation sent successfully',
    fail: {
      email: (email: string) =>
        `Email address ${email} already exists. Please enter a different email.`,
      default: 'Failed to create invitation',
    },
  },
  revokeInvitation: {
    success: 'Invitation revoked successfully',
    fail: 'Failed to revoke invitation',
  },
};

export function* fetchInvitationsListWorker(
  action: InvitationsListFetchAction
) {
  try {
    const response: Resp<typeof api.fetchInvitationsList> = yield call(
      api.fetchInvitationsList,
      { sponsorId: action.payload.sponsorId }
    );
    yield dispatch(actions.fetchInvitationsListSuccess(response.data));
  } catch (error) {
    yield dispatch(
      actions.fetchInvitationsListFailure(error as AxiosError<ApiError>)
    );
  }
}

export function* fetchInvitationSponsorsWorker() {
  try {
    const response: Resp<typeof api.fetchInvitationSponsors> = yield call(
      api.fetchInvitationSponsors
    );
    yield dispatch(actions.fetchInvitationSponsorsSuccess(response.data));
  } catch (error) {
    yield dispatch(
      actions.fetchInvitationSponsorsFailure(error as AxiosError<ApiError>)
    );
  }
}

export function* createInvitationWorker(
  action: PayloadAction<CreateInvitationRequest>
) {
  try {
    yield call(api.createInvitation, action.payload);
    yield call(runToast, 'success', messages.createInvitation.success);
    yield dispatch(actions.createInvitationSuccess());
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      yield call(
        runToast,
        'error',
        error.response?.data?.detail ||
          messages.createInvitation.fail.email(action.payload.email)
      );
    } else {
      yield call(runToast, 'error', messages.createInvitation.fail.default);
    }
    yield dispatch(
      actions.createInvitationFailure(error as AxiosError<ApiError>)
    );
  }
}

export function* revokeInvitationWorker(action: PayloadAction<string>) {
  try {
    yield call(api.revokeInvitation, action.payload);
    yield call(runToast, 'success', messages.revokeInvitation.success);
    yield dispatch(actions.revokeInvitationSuccess());
  } catch (error) {
    yield call(runToast, 'error', messages.revokeInvitation.fail);
    yield dispatch(
      actions.revokeInvitationFailure(error as AxiosError<ApiError>)
    );
  }
}

export function* invitationsUserManagementSaga() {
  yield takeLatest(
    actions.fetchInvitationsList.type,
    fetchInvitationsListWorker
  );
  yield takeLatest(
    actions.fetchInvitationSponsors.type,
    fetchInvitationSponsorsWorker
  );
  yield takeLatest(actions.createInvitation.type, createInvitationWorker);
  yield takeLatest(actions.revokeInvitation.type, revokeInvitationWorker);
}
